import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import NavBar from '../components/Navbar/NavBar';
import Footer from '../components/Footer';
import {useDocTitle} from '../components/CustomHook';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const ServicesCarousel = () => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [itemsPerView, setItemsPerView] = useState(4);

    const services = [
        {
            id: 1,
            title: "Achat Media",
            description: "Gestion de l'allocation du budget d'achat d'espace publicitaire sur tous supports : TV, Radio, Display, Social Media, OOH/DOOH, Print et plus.",
            items: ["TV, Radio, Display", "Social Media & OOH/DOOH", "Print & Email Marketing", "Sponsoring & Placement"],
            slug: "achat-media"
        },
        {
            id: 2,
            title: "Production Audiovisuelle",
            description: "Création de contenus audiovisuels de qualité : spots publicitaires, vidéos institutionnelles, documentaires, films et post-production.",
            items: ["Spots publicitaires", "Vidéos institutionnelles", "Documentaires & Films", "Post-production & Montage"],
            slug: "production-audiovisuelle"
        },
        {
            id: 3,
            title: "Digital Media",
            description: "Gestion des réseaux sociaux, création et gestion de campagnes, SEO & SEA, planification de contenu et optimisation de performance.",
            items: ["Gestion réseaux sociaux", "Création de campagnes", "SEO & SEA", "Planification & Optimisation"],
            slug: "digital-media"
        },
        {
            id: 4,
            title: "Consulting & Stratégie",
            description: "Conseils stratégiques en marketing et communication, élaboration de plans média optimisés et recommandations personnalisées.",
            items: ["Stratégie marketing", "Plans média optimisés", "Recommandations personnalisées", "Analyse des données"],
            slug: "consulting-strategie"
        },
        {
            id: 5,
            title: "Design & Créativité",
            description: "Création d'identités visuelles, logos, supports imprimés, design web et visuels innovants alignés avec votre marque.",
            items: ["Identités visuelles", "Logos & Branding", "Design web & Print", "Créations innovantes"],
            slug: "design-creativite"
        },
        {
            id: 6,
            title: "Événementielle",
            description: "Organisation d'événements variés, création de contenu événementiel, intégration de technologies innovantes et mesure de performance.",
            items: ["Organisation d'événements", "Contenu événementiel", "Technologies innovantes", "Mesure de performance"],
            slug: "evenementielle"
        },
        {
            id: 7,
            title: "Animation 2D & 3D",
            description: "Studio d'animation spécialisé en création 2D et 3D pour publicités, contenus promotionnels et visuels captivants.",
            items: ["Animation 2D & 3D", "Modélisation & CGI", "Contenus promotionnels", "Visuels captivants"],
            slug: "animation-2d-3d"
        },
        {
            id: 8,
            title: "Influence Marketing",
            description: "Collaboration stratégique avec influenceurs, création de contenus uniques et mesure transparente du ROI.",
            items: ["Collaboration influenceurs", "Création de contenus", "Mesure ROI transparente", "Stratégies personnalisées"],
            slug: "influence-marketing"
        }
    ];

    useEffect(() => {
        const updateItemsPerView = () => {
            if (window.innerWidth < 640) {
                setItemsPerView(1);
            } else if (window.innerWidth < 1024) {
                setItemsPerView(2);
            } else {
                setItemsPerView(4);
            }
        };

        updateItemsPerView();
        window.addEventListener('resize', updateItemsPerView);
        return () => window.removeEventListener('resize', updateItemsPerView);
    }, []);

    const totalSlides = Math.ceil(services.length / itemsPerView);

    const nextSlide = () => {
        setCurrentSlide((prev) => (prev + 1) % totalSlides);
    };

    const prevSlide = () => {
        setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
    };

    const goToSlide = (index) => {
        setCurrentSlide(index);
    };

    return (
        <motion.div 
            className="relative mb-16"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
        >
            {/* Navigation Arrows */}
            {totalSlides > 1 && (
                <>
                    <button
                        onClick={prevSlide}
                        className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-white dark:bg-[#111111] rounded-full shadow-lg hover:shadow-xl transition-all duration-300 p-3 -ml-4 border border-gray-200 dark:border-[#262626]"
                        disabled={totalSlides <= 1}
                    >
                        <svg className="w-6 h-6 text-gray-900 dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>

                    <button
                        onClick={nextSlide}
                        className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-white dark:bg-[#111111] rounded-full shadow-lg hover:shadow-xl transition-all duration-300 p-3 -mr-4 border border-gray-200 dark:border-[#262626]"
                        disabled={totalSlides <= 1}
                    >
                        <svg className="w-6 h-6 text-gray-900 dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                </>
            )}

            {/* Carousel Items */}
            <div className="overflow-hidden">
                <div 
                    className="flex transition-transform duration-500 ease-in-out"
                    style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                >
                    {Array.from({ length: totalSlides }, (_, slideIndex) => (
                        <div key={slideIndex} className="w-full flex-shrink-0">
                            <div className={`grid gap-8 ${
                                itemsPerView === 1 ? 'grid-cols-1' : 
                                itemsPerView === 2 ? 'grid-cols-2' : 
                                'grid-cols-4'
                            }`}>
                                {services.slice(slideIndex * itemsPerView, (slideIndex + 1) * itemsPerView).map((service) => (
                                    <div key={service.id} className="bg-white dark:bg-[#111111] rounded-2xl shadow-xl border border-gray-200 dark:border-[#262626] p-8 hover:shadow-2xl transition duration-300 hover:scale-105">
                                        <div className="text-center mb-6">
                                            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{service.title}</h3>
                                        </div>
                                        <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
                                            {service.description}
                                        </p>
                                        <ul className="text-sm text-gray-600 dark:text-gray-300 mb-6 space-y-2">
                                            {service.items.map((item, idx) => (
                                                <li key={idx}>• {item}</li>
                                            ))}
                                        </ul>
                                        <Link 
                                            to={`/get-demo?service=${service.slug}`}
                                            className="w-full bg-gray-900 dark:bg-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100 text-white font-bold py-3 px-6 rounded-xl text-center block transition duration-300"
                                        >
                                            Demander une Démo
                                        </Link>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Dots Indicator */}
            {totalSlides > 1 && (
                <div className="flex justify-center mt-8 space-x-2">
                    {Array.from({ length: totalSlides }, (_, index) => (
                        <button
                            key={index}
                            onClick={() => goToSlide(index)}
                            className={`w-3 h-3 rounded-full transition-all duration-300 ${
                                index === currentSlide 
                                    ? 'bg-gray-900 dark:bg-white scale-125' 
                                    : 'bg-gray-300 dark:bg-[#262626] hover:bg-gray-400 dark:hover:bg-[#333333]'
                            }`}
                        />
                    ))}
                </div>
            )}
        </motion.div>
    );
};

const Services = () => {
    useDocTitle('Tomorrow Global - Services')

    return (
        <>
            <SEO 
                title="Nos Services - Tomorrow Global | Achat Média, Production, Digital"
                description="Découvrez nos expertises : Achat Media, Production Audiovisuelle, Digital Media, Consulting & Stratégie, Design & Créativité, Événementielle, Animation 2D & 3D, Influence Marketing. Chez Tomorrow, notre objectif premier est de servir la performance."
                keywords="achat média, production audiovisuelle, digital media, consulting stratégie, design créativité, événementielle, animation 2D 3D, influence marketing, gestion réseaux sociaux, SEO SEA, stratégie communication"
            />
            <div>
                <NavBar />
            </div>
            
            <div className="min-h-screen bg-gray-50 dark:bg-black pt-24 md:pt-28">
                <div className="container mx-auto px-4 lg:px-20">
                    {/* Back Button */}
                    <motion.div 
                        className="mb-8 mt-4"
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <Link 
                            to="/" 
                            className="inline-flex items-center text-gray-900 dark:text-white hover:text-gray-700 dark:hover:text-gray-300 font-semibold transition duration-300"
                        >
                            <motion.svg 
                                className="w-5 h-5 mr-2" 
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                                whileHover={{ x: -5 }}
                                transition={{ duration: 0.2 }}
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </motion.svg>
                            Retour à l'accueil
                        </Link>
                    </motion.div>

                    {/* Hero Section */}
                    <motion.div 
                        className="text-center mb-16"
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.8 }}
                    >
                        <motion.h1 
                            className="text-4xl md:text-6xl font-black text-gray-900 dark:text-white mb-6"
                        >
                            Nos Expertises
                        </motion.h1>
                        <motion.p 
                            className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto"
                        >
                            Chez Tomorrow, notre objectif premier est de servir la performance et créer une croissance durable dans la notoriété de nos clients.
                        </motion.p>
                    </motion.div>

                    {/* Services Carousel */}
                    <ServicesCarousel />

                    {/* Process Section */}
                    <motion.div 
                        className="bg-white dark:bg-[#111111] rounded-2xl shadow-xl border border-gray-200 dark:border-[#262626] p-8 mb-16"
                        initial={{ opacity: 0, y: 50, scale: 0.9 }}
                        whileInView={{ opacity: 1, y: 0, scale: 1 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.8 }}
                    >
                        <motion.h2 
                            className="text-3xl font-bold text-gray-900 dark:text-white text-center mb-12"
                        >
                            Notre Processus de Développement
                        </motion.h2>
                        <motion.div 
                            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
                            variants={{
                                hidden: { opacity: 0 },
                                visible: {
                                    opacity: 1,
                                    transition: {
                                        staggerChildren: 0.2
                                    }
                                }
                            }}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                        >
                            <motion.div 
                                className="text-center"
                                variants={{
                                    hidden: { opacity: 0, y: 20 },
                                    visible: { opacity: 1, y: 0 }
                                }}
                            >
                                <motion.div 
                                    className="bg-gray-200 dark:bg-[#171717] rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4"
                                    whileHover={{ scale: 1.1, rotate: 360 }}
                                    transition={{ duration: 0.5 }}
                                >
                                    <span className="text-2xl font-bold text-gray-900 dark:text-white">1</span>
                                </motion.div>
                                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Analyse</h3>
                                <p className="text-gray-600 dark:text-gray-300">Nous analysons vos besoins et définissons les spécifications du projet.</p>
                            </motion.div>
                            <motion.div 
                                className="text-center"
                                variants={{
                                    hidden: { opacity: 0, y: 20 },
                                    visible: { opacity: 1, y: 0 }
                                }}
                            >
                                <motion.div 
                                    className="bg-gray-200 dark:bg-[#171717] rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4"
                                    whileHover={{ scale: 1.1, rotate: 360 }}
                                    transition={{ duration: 0.5 }}
                                >
                                    <span className="text-2xl font-bold text-gray-900 dark:text-white">2</span>
                                </motion.div>
                                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Conception</h3>
                                <p className="text-gray-600 dark:text-gray-300">Nous créons l'architecture et le design de votre solution.</p>
                            </motion.div>
                            <motion.div 
                                className="text-center"
                                variants={{
                                    hidden: { opacity: 0, y: 20 },
                                    visible: { opacity: 1, y: 0 }
                                }}
                            >
                                <motion.div 
                                    className="bg-gray-200 dark:bg-[#171717] rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4"
                                    whileHover={{ scale: 1.1, rotate: 360 }}
                                    transition={{ duration: 0.5 }}
                                >
                                    <span className="text-2xl font-bold text-gray-900 dark:text-white">3</span>
                                </motion.div>
                                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Développement</h3>
                                <p className="text-gray-600 dark:text-gray-300">Nous développons votre solution avec les meilleures pratiques.</p>
                            </motion.div>
                            <motion.div 
                                className="text-center"
                                variants={{
                                    hidden: { opacity: 0, y: 20 },
                                    visible: { opacity: 1, y: 0 }
                                }}
                            >
                                <motion.div 
                                    className="bg-gray-200 dark:bg-[#171717] rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4"
                                    whileHover={{ scale: 1.1, rotate: 360 }}
                                    transition={{ duration: 0.5 }}
                                >
                                    <span className="text-2xl font-bold text-gray-900 dark:text-white">4</span>
                                </motion.div>
                                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Déploiement</h3>
                                <p className="text-gray-600 dark:text-gray-300">Nous déployons et maintenons votre solution.</p>
                            </motion.div>
                        </motion.div>
                    </motion.div>

                    {/* Why Choose Us */}
                    <motion.div 
                        className="grid lg:grid-cols-2 gap-12 mb-16"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.8 }}
                    >
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                        >
                            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">Pourquoi Choisir Tomorrow Global ?</h2>
                            <div className="space-y-6">
                                <div className="flex items-start">
                                    <div className="bg-gray-200 dark:bg-[#171717] rounded-full p-3 mr-4">
                                        <svg className="w-6 h-6 text-gray-900 dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Plus de 10 ans d'expérience</h3>
                                        <p className="text-gray-600 dark:text-gray-300">Une expertise approfondie en analyse et conception de logiciels.</p>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <div className="bg-gray-200 dark:bg-[#171717] rounded-full p-3 mr-4">
                                        <svg className="w-6 h-6 text-gray-900 dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Solutions personnalisées</h3>
                                        <p className="text-gray-600 dark:text-gray-300">Chaque projet est adapté aux besoins spécifiques de votre entreprise.</p>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <div className="bg-gray-200 dark:bg-[#171717] rounded-full p-3 mr-4">
                                        <svg className="w-6 h-6 text-gray-900 dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Support 24/7</h3>
                                        <p className="text-gray-600 dark:text-gray-300">Notre équipe est disponible pour vous accompagner à tout moment.</p>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                        <motion.div 
                            className="bg-gray-100 dark:bg-[#111111] rounded-2xl border border-gray-200 dark:border-[#262626] p-8"
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                        >
                            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Prêt à commencer ?</h3>
                            <p className="text-gray-700 dark:text-gray-300 mb-6">
                                Contactez-nous dès aujourd'hui pour discuter de votre projet et découvrir comment nous pouvons vous aider à atteindre vos objectifs.
                            </p>
                            <div className="space-y-4">
                                <Link 
                                    to="/contact" 
                                    className="w-full bg-gray-900 dark:bg-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100 text-white font-bold py-4 px-6 rounded-xl text-center block transition duration-300"
                                >
                                    Demander un Devis Gratuit
                                </Link>
                                <Link 
                                    to="/portfolio" 
                                    className="w-full bg-transparent border-2 border-gray-900 dark:border-white text-gray-900 dark:text-white hover:bg-gray-900 dark:hover:bg-white hover:text-white dark:hover:text-gray-900 font-bold py-4 px-6 rounded-xl text-center block transition duration-300"
                                >
                                    Voir Notre Portfolio
                                </Link>
                            </div>
                        </motion.div>
                    </motion.div>

                    {/* CTA Section */}
                    <motion.div 
                        className="bg-gray-900 dark:bg-white rounded-2xl p-8 text-center text-white dark:text-gray-900"
                        initial={{ opacity: 0, y: 50, scale: 0.9 }}
                        whileInView={{ opacity: 1, y: 0, scale: 1 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.8 }}
                    >
                        <h2 className="text-3xl font-bold mb-4">Transformons vos idées en réalité</h2>
                        <p className="text-xl mb-8 opacity-90 dark:opacity-80">
                            Avec notre expertise et notre engagement, nous créons des solutions qui font la différence.
                        </p>
                        <Link 
                            to="/contact" 
                            className="bg-white dark:bg-gray-900 text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 font-bold py-4 px-8 rounded-xl transition duration-300 inline-block"
                        >
                            Commencer Votre Projet
                        </Link>
                    </motion.div>
                </div>
            </div>

            <Footer />
        </>
    )
}

export default Services;
